import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { get, put } from 'aws-amplify/api';
import { useAuth } from './AuthContext';
import { VALID_POSITIONS } from '../constants/positions';

const AppSettingsContext = createContext();

export const useAppSettings = () => {
  const context = useContext(AppSettingsContext);
  if (!context) {
    throw new Error('useAppSettings must be used within an AppSettingsProvider');
  }
  return context;
};

export const AppSettingsProvider = ({ children }) => {
  const { user, getAuthToken } = useAuth();
  const [dataSource, setDataSourceState] = useState('simulator');
  const [comingSoonEnabled, setComingSoonState] = useState(false);
  const [comingSoonOpeningDate, setComingSoonOpeningDateState] = useState(null);
  const [hiringEnabled, setHiringEnabledState] = useState(true);
  const [openPositions, setOpenPositionsState] = useState(VALID_POSITIONS);
  const [happyHourSchedule, setHappyHourScheduleState] = useState(null);
  const [eventRequestNotificationEmail, setEventRequestNotificationEmailState] = useState('');
  const [eventRequestNotificationEmailFallback, setEventRequestNotificationEmailFallback] = useState('Events@ChulasPL.com');
  const [deploymentStage, setDeploymentStage] = useState('prod');
  const [loading, setLoading] = useState(true);
  const [switchingSource, setSwitchingSource] = useState(false);
  const [switchingComingSoon, setSwitchingComingSoon] = useState(false);
  const [switchingOpeningDate, setSwitchingOpeningDate] = useState(false);
  const [switchingHiring, setSwitchingHiring] = useState(false);
  const [switchingPositions, setSwitchingPositions] = useState(false);
  const [savingHappyHour, setSavingHappyHour] = useState(false);
  const [savingEventRequestNotificationEmail, setSavingEventRequestNotificationEmail] = useState(false);

  const isSimulator = dataSource === 'simulator';

  const coerceBool = (v) => {
    if (typeof v === 'boolean') return v;
    if (typeof v === 'string') return ['true', '1', 'yes', 'enabled', 'on'].includes(v.trim().toLowerCase());
    return false;
  };

  const fetchSettings = useCallback(async () => {
    try {
      setLoading(true);
      const token = await getAuthToken();
      if (!token) return;
      const restOperation = get({
        apiName: 'ChulasAdminAPI',
        path: '/v1/admin/settings',
        options: { headers: { Authorization: token } },
      });
      const response = await restOperation.response;
      const data = await response.body.json();
      setDataSourceState(data.menu_source || 'simulator');
      setComingSoonState(coerceBool(data.coming_soon));
      setComingSoonOpeningDateState(data.coming_soon_opening_date || null);
      setHiringEnabledState(coerceBool(data.hiring_enabled) ?? true);
      setOpenPositionsState(
        Array.isArray(data.open_positions) ? data.open_positions : VALID_POSITIONS
      );
      setHappyHourScheduleState(data.happy_hour_schedule || null);
      setEventRequestNotificationEmailState(data.event_request_notification_email || '');
      setEventRequestNotificationEmailFallback(
        data._event_request_notification_email_fallback || 'Events@ChulasPL.com'
      );
      setDeploymentStage(data._deployment_stage || 'prod');
    } catch (err) {
      console.error('Error fetching settings:', err);
      setDataSourceState('simulator');
      setComingSoonState(false);
      setHiringEnabledState(true);
      setOpenPositionsState(VALID_POSITIONS);
      setEventRequestNotificationEmailState('');
    } finally {
      setLoading(false);
    }
  }, [getAuthToken]);

  useEffect(() => {
    if (user) {
      fetchSettings();
    } else {
      setLoading(false);
    }
  }, [user, fetchSettings]);

  const setDataSource = async (newSource) => {
    try {
      setSwitchingSource(true);
      const token = await getAuthToken();
      if (!token) throw new Error('Authentication token not found');
      const restOperation = put({
        apiName: 'ChulasAdminAPI',
        path: '/v1/admin/settings',
        options: {
          headers: { Authorization: token },
          body: { setting_key: 'menu_source', value: newSource },
        },
      });
      await restOperation.response;
      setDataSourceState(newSource);
    } finally {
      setSwitchingSource(false);
    }
  };

  const setComingSoon = async (enabled) => {
    try {
      setSwitchingComingSoon(true);
      const token = await getAuthToken();
      if (!token) throw new Error('Authentication token not found');
      const restOperation = put({
        apiName: 'ChulasAdminAPI',
        path: '/v1/admin/settings',
        options: {
          headers: { Authorization: token },
          body: { setting_key: 'coming_soon', value: !!enabled },
        },
      });
      await restOperation.response;
      setComingSoonState(!!enabled);
    } finally {
      setSwitchingComingSoon(false);
    }
  };

  const setComingSoonOpeningDate = async (dateStr) => {
    try {
      setSwitchingOpeningDate(true);
      const token = await getAuthToken();
      if (!token) throw new Error('Authentication token not found');
      const value = dateStr && dateStr.trim() ? dateStr : null;
      const restOperation = put({
        apiName: 'ChulasAdminAPI',
        path: '/v1/admin/settings',
        options: {
          headers: { Authorization: token },
          body: { setting_key: 'coming_soon_opening_date', value },
        },
      });
      await restOperation.response;
      setComingSoonOpeningDateState(value);
    } finally {
      setSwitchingOpeningDate(false);
    }
  };

  const setHappyHourSchedule = async (schedule) => {
    // schedule = null clears the setting; otherwise { days: ["mon",...], start, end, note }
    try {
      setSavingHappyHour(true);
      const token = await getAuthToken();
      if (!token) throw new Error('Authentication token not found');
      const restOperation = put({
        apiName: 'ChulasAdminAPI',
        path: '/v1/admin/settings',
        options: {
          headers: { Authorization: token },
          body: { setting_key: 'happy_hour_schedule', value: schedule },
        },
      });
      await restOperation.response;
      setHappyHourScheduleState(schedule);
    } finally {
      setSavingHappyHour(false);
    }
  };

  const setEventRequestNotificationEmail = async (email) => {
    const normalized = String(email || '').trim().toLowerCase();
    try {
      setSavingEventRequestNotificationEmail(true);
      const token = await getAuthToken();
      if (!token) throw new Error('Authentication token not found');
      const operation = put({
        apiName: 'ChulasAdminAPI',
        path: '/v1/admin/settings',
        options: {
          headers: { Authorization: token },
          body: {
            setting_key: 'event_request_notification_email',
            value: normalized,
          },
        },
      });
      await operation.response;
      setEventRequestNotificationEmailState(normalized);
    } finally {
      setSavingEventRequestNotificationEmail(false);
    }
  };

  const setHiringEnabled = async (enabled) => {
    try {
      setSwitchingHiring(true);
      const token = await getAuthToken();
      if (!token) throw new Error('Authentication token not found');
      const restOperation = put({
        apiName: 'ChulasAdminAPI',
        path: '/v1/admin/settings',
        options: {
          headers: { Authorization: token },
          body: { setting_key: 'hiring_enabled', value: !!enabled },
        },
      });
      await restOperation.response;
      setHiringEnabledState(!!enabled);
    } finally {
      setSwitchingHiring(false);
    }
  };

  const setOpenPositions = async (positions) => {
    const list = (Array.isArray(positions) ? positions : []).filter((p) =>
      VALID_POSITIONS.includes(p)
    );
    try {
      setSwitchingPositions(true);
      const token = await getAuthToken();
      if (!token) throw new Error('Authentication token not found');
      const restOperation = put({
        apiName: 'ChulasAdminAPI',
        path: '/v1/admin/settings',
        options: {
          headers: { Authorization: token },
          body: { setting_key: 'open_positions', value: list },
        },
      });
      await restOperation.response;
      setOpenPositionsState(list);
    } finally {
      setSwitchingPositions(false);
    }
  };

  const value = {
    dataSource,
    isSimulator,
    loading,
    switchingSource,
    setDataSource,
    comingSoonEnabled,
    switchingComingSoon,
    setComingSoon,
    comingSoonOpeningDate,
    switchingOpeningDate,
    setComingSoonOpeningDate,
    hiringEnabled,
    switchingHiring,
    setHiringEnabled,
    openPositions,
    switchingPositions,
    setOpenPositions,
    happyHourSchedule,
    savingHappyHour,
    setHappyHourSchedule,
    eventRequestNotificationEmail,
    eventRequestNotificationEmailFallback,
    deploymentStage,
    savingEventRequestNotificationEmail,
    setEventRequestNotificationEmail,
  };

  return (
    <AppSettingsContext.Provider value={value}>
      {children}
    </AppSettingsContext.Provider>
  );
};
